import React, { useState, useEffect, useRef, useCallback } from 'react';
import 'reactflow/dist/style.css';
import { Layout, Input, Avatar, Button, Typography, Card, Dropdown, Space, Spin, Empty, Modal, Form, message, Radio, Select, Upload, Drawer, Tabs, Tooltip, Popover, Progress } from 'antd';
import { SearchOutlined, BellOutlined, AppstoreOutlined, BarsOutlined, PlusOutlined, MoreOutlined, FileImageOutlined, UserOutlined, LoadingOutlined, CloseOutlined, LayoutOutlined, MobileOutlined, UploadOutlined, InboxOutlined, BulbOutlined, FileTextOutlined, FolderOutlined, DeleteOutlined, EyeOutlined, PlaySquareOutlined, AudioOutlined, BgColorsOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCanvases, useCreateCanvas, useDeleteCanvas, useUpdateCanvas, useCanvasGeneratedMedia } from '../../api/canvasApi';
import { useAuth } from '../../context/AuthContext';
import MediaGallery from './components/MediaGallery';
import { ReactComponent as Logo } from '../../images/pxlplygrnd-logo.svg';
import PromptLibraryDrawer from '../editor/components/PromptLibraryDrawer';
import AssetsDrawer from './components/AssetsDrawer';
import ColorPalettesPopover from '../editor/components/ColorPalettesPopover';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

// Format relative time
const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Just now';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
};

// Image Slideshow Component for Canvas Preview
const ImageSlideshow = ({ canvasId }) => {
    const { data: media = [], isLoading } = useCanvasGeneratedMedia(canvasId);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Filter for images only
    const images = media.filter(m => m.type === 'image' && m.url);

    // Auto-cycle through images
    useEffect(() => {
        if (images.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % images.length);
        }, 3000); // Change image every 3 seconds

        return () => clearInterval(interval);
    }, [images.length]);

    if (isLoading) {
        return (
            <div style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <LoadingOutlined style={{ fontSize: 24, color: '#30363d' }} />
            </div>
        );
    }

    if (images.length === 0) {
        return (
            <div style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
            }}>
                <FileImageOutlined style={{ fontSize: 32, color: '#30363d', marginBottom: 8 }} />
                <Text style={{ fontSize: 11, color: '#484f58' }}>No images yet</Text>
            </div>
        );
    }

    const currentImage = images[currentIndex];

    return (
        <div style={{
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <img
                src={currentImage.url}
                alt=""
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'opacity 0.5s ease'
                }}
            />
            {/* Image counter badge */}
            {images.length > 1 && (
                <div style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 4
                }}>
                    {currentIndex + 1} / {images.length}
                </div>
            )}
            {/* Progress dots */}
            {images.length > 1 && images.length <= 5 && (
                <div style={{
                    position: 'absolute',
                    bottom: 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 4
                }}>
                    {images.map((_, idx) => (
                        <div
                            key={idx}
                            style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: idx === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)',
                                transition: 'background 0.3s ease'
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const ProjectCard = ({ canvas, onClick, viewMode, onDelete, onRename }) => {
    const menuItems = [
        {
            label: 'Rename',
            key: 'rename',
            onClick: ({ domEvent }) => {
                domEvent.stopPropagation();
                onRename(canvas);
            }
        },
        {
            label: 'Delete',
            key: 'delete',
            danger: true,
            onClick: ({ domEvent }) => {
                domEvent.stopPropagation();
                onDelete(canvas.id);
            }
        }
    ];

    if (viewMode === 'grid') {
        // Grid view: Full-bleed image with text overlay
        return (
            <Card
                hoverable
                style={{
                    width: '100%',
                    borderRadius: 10,
                    background: '#0d1117',
                    border: '1px solid #3d444d',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                    padding: 0
                }}
                bodyStyle={{ padding: 0 }}
                onClick={onClick}
            >
                <div style={{
                    position: 'relative',
                    height: 200,
                    width: '100%'
                }}>
                    {/* Full-size image */}
                    <div style={{
                        position: 'absolute',
                        inset: 0
                    }}>
                        <ImageSlideshow canvasId={canvas.id} />
                    </div>

                    {/* Gradient overlay for text readability */}
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0) 70%)',
                        pointerEvents: 'none'
                    }} />

                    {/* Text overlay */}
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end'
                    }}>
                        <div>
                            <Text strong style={{ display: 'block', color: '#fff', fontSize: 14, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>{canvas.name}</Text>
                            <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>Edited {formatRelativeTime(canvas.updated_at)}</Text>
                        </div>
                        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                            <Button
                                type="text"
                                icon={<MoreOutlined style={{ color: '#fff' }} />}
                                size="small"
                                onClick={e => e.stopPropagation()}
                                style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 4 }}
                            />
                        </Dropdown>
                    </div>
                </div>
            </Card>
        );
    }

    // List view: Keep existing compact layout
    return (
        <Card
            hoverable
            style={{
                width: '100%',
                borderRadius: 10,
                background: '#161b22',
                border: '1px solid #3d444d',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                marginBottom: 16,
                overflow: 'hidden'
            }}
            bodyStyle={{
                padding: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 16
            }}
            onClick={onClick}
        >
            <div style={{
                width: 80,
                height: 48,
                background: '#0d1117',
                borderRadius: 4,
                overflow: 'hidden',
                flexShrink: 0,
                border: '1px solid #30363d'
            }}>
                <ImageSlideshow canvasId={canvas.id} />
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Text strong style={{ display: 'block', color: '#c9d1d9', fontSize: 14 }}>{canvas.name}</Text>
                    <Text style={{ fontSize: 12, color: '#8b949e' }}>Edited {formatRelativeTime(canvas.updated_at)}</Text>
                </div>
                <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                    <Button type="text" icon={<MoreOutlined style={{ color: '#8b949e' }} />} size="small" onClick={e => e.stopPropagation()} />
                </Dropdown>
            </div>
        </Card>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [viewMode, setViewMode] = useState('grid');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [selectedCanvas, setSelectedCanvas] = useState(null);
    const [form] = Form.useForm();
    const [renameForm] = Form.useForm();

    // Inspiration upload state
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState('branding');
    const [uploading, setUploading] = useState(false);
    const [uploadNotes, setUploadNotes] = useState('');
    const [uploadFileList, setUploadFileList] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

    // Drawer state
    const [promptsDrawerVisible, setPromptsDrawerVisible] = useState(false);
    const [assetsDrawerVisible, setAssetsDrawerVisible] = useState(false);
    const [colorPalettesVisible, setColorPalettesVisible] = useState(false);

    const inspirationFolders = [
        { value: 'branding', label: 'Branding' },
        { value: 'effects', label: 'Effects' },
        { value: 'midjourney', label: 'Midjourney' },
        { value: 'people', label: 'People' },
        { value: 'places', label: 'Places' },
        { value: 'things', label: 'Things' }
    ];

    // Query hooks
    const { data: canvases = [], isLoading, error } = useCanvases();
    const createCanvas = useCreateCanvas();
    const deleteCanvas = useDeleteCanvas();
    const updateCanvas = useUpdateCanvas();

    const openProject = (id) => {
        navigate(`/canvas/${id}`);
    };

    const handleCreateCanvas = async (values) => {
        try {
            const canvas = await createCanvas.mutateAsync({
                name: values.name,
                description: values.description || '',
                canvasType: values.canvasType || 'media',
                system: { id: 'system', email: 'system@pxlplygrnd.com' }
            });
            message.success('Canvas created!');
            setCreateModalOpen(false);
            form.resetFields();
            openProject(canvas.id);
        } catch (err) {
            message.error('Failed to create canvas');
        }
    };

    const handleDeleteCanvas = async (canvasId) => {
        Modal.confirm({
            title: 'Delete Canvas',
            content: 'Are you sure you want to delete this canvas? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteCanvas.mutateAsync(canvasId);
                    message.success('Canvas deleted');
                } catch (err) {
                    message.error('Failed to delete canvas');
                }
            }
        });
    };

    const handleRename = (canvas) => {
        setSelectedCanvas(canvas);
        renameForm.setFieldsValue({ name: canvas.name });
        setRenameModalOpen(true);
    };

    const handleRenameSubmit = async (values) => {
        try {
            await updateCanvas.mutateAsync({
                canvasId: selectedCanvas.id,
                data: {
                    name: values.name,
                    system: { id: 'system', email: 'system@pxlplygrnd.com' }
                }
            });
            message.success('Canvas renamed');
            setRenameModalOpen(false);
            setSelectedCanvas(null);
        } catch (err) {
            message.error('Failed to rename canvas');
        }
    };

    const uploadIdCounter = useRef(0);

    // Accumulate files into the list without uploading
    const handleBeforeUpload = (file, fileList) => {
        // Ant calls beforeUpload once per file in the batch, each time with the full list.
        // Only process on the first file to avoid adding the batch multiple times.
        if (file === fileList[0]) {
            setUploadFileList(prev => {
                const newFiles = fileList.map(f => {
                    uploadIdCounter.current += 1;
                    return { file: f, id: `upload-${uploadIdCounter.current}`, name: f.name, status: 'pending' };
                });
                return [...prev, ...newFiles];
            });
        }
        return false; // Prevent automatic upload
    };

    // Remove a file from the pending list
    const handleRemoveFile = (id) => {
        setUploadFileList(prev => prev.filter(f => f.id !== id));
    };

    // Upload all files sequentially
    const handleUploadAll = async () => {
        if (uploadFileList.length === 0) return;
        setUploading(true);
        setUploadProgress({ current: 0, total: uploadFileList.length });

        const token = await currentUser?.getIdToken();
        if (!token) {
            message.error('Please sign in to upload');
            setUploading(false);
            return;
        }

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < uploadFileList.length; i++) {
            const entry = uploadFileList[i];
            setUploadProgress({ current: i + 1, total: uploadFileList.length });
            setUploadFileList(prev => prev.map(f => f.id === entry.id ? { ...f, status: 'uploading' } : f));

            try {
                const formData = new FormData();
                formData.append('file', entry.file);
                formData.append('bucket', `inspiration-from-the-web/${selectedFolder}`);
                if (uploadNotes.trim()) {
                    formData.append('notes', uploadNotes.trim());
                }

                const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/storage/upload`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData
                });

                if (!response.ok) throw new Error('Upload failed');
                setUploadFileList(prev => prev.map(f => f.id === entry.id ? { ...f, status: 'done' } : f));
                successCount++;
            } catch (err) {
                console.error('Upload error:', err);
                setUploadFileList(prev => prev.map(f => f.id === entry.id ? { ...f, status: 'error' } : f));
                failCount++;
            }
        }

        setUploading(false);
        if (successCount > 0) {
            message.success(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''} to ${selectedFolder}!`);
        }
        if (failCount > 0) {
            message.error(`${failCount} file${failCount > 1 ? 's' : ''} failed to upload`);
        }
        // Clear completed uploads and close
        if (failCount === 0) {
            setUploadFileList([]);
            setUploadModalOpen(false);
            setUploadNotes('');
            setUploadProgress({ current: 0, total: 0 });
        }
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#0d1117' }}>
            <Header style={{
                background: '#161b22',
                padding: '0 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #30363d',
                height: 64,
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Logo style={{ width: 28, height: 36 }} />
                    <Title level={4} style={{ margin: 0, color: '#fff' }}>pxlplygrnd</Title>
                </div>
                <Space size={12}>
                    <Button
                        icon={<BulbOutlined />}
                        onClick={() => setUploadModalOpen(true)}
                        style={{ background: 'transparent', borderColor: '#30363d', color: '#c9d1d9' }}
                    >
                        Upload Inspiration
                    </Button>
                    <Popover
                        open={colorPalettesVisible}
                        onOpenChange={setColorPalettesVisible}
                        content={<ColorPalettesPopover />}
                        trigger="click"
                        placement="bottomRight"
                        overlayStyle={{ padding: 0 }}
                        overlayInnerStyle={{
                            background: '#161b22',
                            border: '1px solid #30363d',
                            borderRadius: 8,
                            padding: 0,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
                        }}
                    >
                        <Button
                            icon={<BgColorsOutlined />}
                            style={{
                                background: colorPalettesVisible ? 'rgba(88, 166, 255, 0.15)' : 'transparent',
                                borderColor: colorPalettesVisible ? '#58a6ff' : '#30363d',
                                color: colorPalettesVisible ? '#58a6ff' : '#c9d1d9'
                            }}
                        >
                            Colors
                        </Button>
                    </Popover>
                    <Button
                        icon={<FileTextOutlined />}
                        onClick={() => setPromptsDrawerVisible(prev => !prev)}
                        style={{ background: 'transparent', borderColor: '#30363d', color: '#c9d1d9' }}
                    >
                        Prompts
                    </Button>
                    <Button
                        onClick={() => setAssetsDrawerVisible(prev => !prev)}
                        style={{ background: 'transparent', borderColor: '#30363d', color: '#c9d1d9' }}
                    >
                        Assets
                    </Button>
                </Space>
            </Header>
            <Content style={{ padding: '24px 50px', position: 'relative', overflow: 'hidden' }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Space>
                        <Button
                            icon={<AppstoreOutlined />}
                            type="text"
                            style={{ color: viewMode === 'grid' ? '#1890ff' : '#8b949e', background: viewMode === 'grid' ? 'rgba(24, 144, 255, 0.1)' : 'transparent' }}
                            onClick={() => setViewMode('grid')}
                        />
                        <Button
                            icon={<BarsOutlined />}
                            type="text"
                            style={{ color: viewMode === 'list' ? '#1890ff' : '#8b949e', background: viewMode === 'list' ? 'rgba(24, 144, 255, 0.1)' : 'transparent' }}
                            onClick={() => setViewMode('list')}
                        />
                    </Space>
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: 50 }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />} />
                        <Text style={{ display: 'block', marginTop: 16, color: '#8b949e' }}>Loading canvases...</Text>
                    </div>
                ) : error ? (
                    <Empty
                        description={<Text style={{ color: '#8b949e' }}>Failed to load canvases. Is the API running?</Text>}
                        style={{ marginTop: 50 }}
                    />
                ) : (
                    <div style={{
                        display: viewMode === 'grid' ? 'grid' : 'block',
                        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'none',
                        gap: 24
                    }}>
                        {/* Create New Canvas Card */}
                        <Card
                            hoverable
                            style={{
                                border: '2px dashed #30363d',
                                background: '#161b22',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: viewMode === 'grid' ? 243 : 80,
                                marginBottom: viewMode === 'list' ? 16 : 0
                            }}
                            onClick={() => setCreateModalOpen(true)}
                        >
                            <div style={{
                                textAlign: 'center',
                                display: viewMode === 'list' ? 'flex' : 'block',
                                gap: 12,
                                alignItems: 'center'
                            }}>
                                <PlusOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: viewMode === 'grid' ? 8 : 0 }} />
                                <Text style={{ display: 'block', color: '#8b949e' }}>New Canvas</Text>
                            </div>
                        </Card>

                        {/* Canvas Cards */}
                        {canvases.map(canvas => (
                            <ProjectCard
                                key={canvas.id}
                                canvas={canvas}
                                onClick={() => openProject(canvas.id)}
                                viewMode={viewMode}
                                onDelete={handleDeleteCanvas}
                                onRename={handleRename}
                            />
                        ))}
                    </div>

                )}
                {/*<MediaGallery />*/}
                <PromptLibraryDrawer
                    visible={promptsDrawerVisible}
                    onClose={() => setPromptsDrawerVisible(false)}
                    onCopyToCanvas={() => { }}
                />
                <AssetsDrawer
                    visible={assetsDrawerVisible}
                    onClose={() => setAssetsDrawerVisible(false)}
                />
            </Content>

            <Modal

                open={createModalOpen}
                onCancel={() => setCreateModalOpen(false)}
                footer={null}
                title="Create New Canvas"
                width={500}
            >
                <Form form={form} onFinish={handleCreateCanvas} layout="vertical" style={{ marginTop: 16 }} initialValues={{ canvasType: 'media' }}>
                    <Form.Item
                        name="canvasType"
                        label="Canvas Type"
                        rules={[{ required: true, message: 'Please select a canvas type' }]}
                    >
                        <Radio.Group style={{ width: '100%' }}>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <Radio.Button
                                    value="media"
                                    style={{
                                        flex: 1,
                                        height: 100,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 8,
                                        border: '1px solid #30363d'
                                    }}
                                >
                                    <FileImageOutlined style={{ fontSize: 28, marginBottom: 8 }} />
                                    <span style={{ fontWeight: 500 }}>Media Canvas</span>
                                    <span style={{ fontSize: 11, color: '#8b949e' }}>Nodes & AI generation</span>
                                </Radio.Button>
                                <Radio.Button
                                    value="uiDesign"
                                    style={{
                                        flex: 1,
                                        height: 100,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 8,
                                        border: '1px solid #30363d'
                                    }}
                                >
                                    <MobileOutlined style={{ fontSize: 28, marginBottom: 8 }} />
                                    <span style={{ fontWeight: 500 }}>UI Design Canvas</span>
                                    <span style={{ fontSize: 11, color: '#8b949e' }}>Artboards & components</span>
                                </Radio.Button>
                            </div>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label="Canvas Name"
                        rules={[{ required: true, message: 'Please enter a name' }]}
                    >
                        <Input placeholder="My Awesome Canvas" />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description (optional)"
                    >
                        <Input.TextArea placeholder="What's this canvas about?" rows={3} />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={createCanvas.isPending}>Create</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Rename Modal */}
            <Modal
                title="Rename Canvas"
                open={renameModalOpen}
                onCancel={() => setRenameModalOpen(false)}
                footer={null}
            >
                <Form form={renameForm} onFinish={handleRenameSubmit} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="name"
                        label="Canvas Name"
                        rules={[{ required: true, message: 'Please enter a name' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setRenameModalOpen(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={updateCanvas.isPending}>Save</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Inspiration Upload Modal */}
            <Modal
                title="Upload Inspiration"
                open={uploadModalOpen}
                onCancel={() => { if (!uploading) { setUploadModalOpen(false); setUploadFileList([]); setUploadNotes(''); setUploadProgress({ current: 0, total: 0 }); } }}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#8b949e', fontSize: 12 }}>
                            {uploading
                                ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}…`
                                : uploadFileList.length > 0
                                    ? `${uploadFileList.length} file${uploadFileList.length > 1 ? 's' : ''} selected`
                                    : ''}
                        </span>
                        <Space>
                            <Button onClick={() => { if (!uploading) { setUploadModalOpen(false); setUploadFileList([]); setUploadNotes(''); setUploadProgress({ current: 0, total: 0 }); } }} disabled={uploading}>Cancel</Button>
                            <Button type="primary" onClick={handleUploadAll} loading={uploading} disabled={uploadFileList.length === 0 || uploading}>
                                Upload{uploadFileList.length > 0 ? ` ${uploadFileList.length} File${uploadFileList.length > 1 ? 's' : ''}` : ''}
                            </Button>
                        </Space>
                    </div>
                }
                width={500}
            >
                <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 8, color: '#c9d1d9', fontWeight: 500 }}>Folder</label>
                        <Select
                            value={selectedFolder}
                            onChange={setSelectedFolder}
                            style={{ width: '100%' }}
                            options={inspirationFolders}
                            disabled={uploading}
                        />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 8, color: '#c9d1d9', fontWeight: 500 }}>Notes (optional)</label>
                        <Input.TextArea
                            value={uploadNotes}
                            onChange={(e) => setUploadNotes(e.target.value)}
                            placeholder="Add notes about this inspiration..."
                            rows={2}
                            style={{ background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}
                            disabled={uploading}
                        />
                    </div>
                    <Upload.Dragger
                        name="file"
                        multiple={true}
                        beforeUpload={handleBeforeUpload}
                        showUploadList={false}
                        accept="image/*,video/*"
                        disabled={uploading}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                        </p>
                        <p className="ant-upload-text" style={{ color: '#c9d1d9' }}>
                            Click or drag files to add
                        </p>
                        <p className="ant-upload-hint" style={{ color: '#8b949e' }}>
                            Select multiple images and videos
                        </p>
                    </Upload.Dragger>

                    {/* File list */}
                    {uploadFileList.length > 0 && (
                        <div style={{ marginTop: 12, maxHeight: 200, overflowY: 'auto' }}>
                            {uploadFileList.map(entry => (
                                <div key={entry.id} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '6px 8px', borderRadius: 6,
                                    background: entry.status === 'error' ? 'rgba(255,77,79,0.08)' : '#161b22',
                                    marginBottom: 4, border: '1px solid #30363d'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                        {entry.status === 'uploading' && <LoadingOutlined style={{ color: '#1890ff', fontSize: 14 }} spin />}
                                        {entry.status === 'done' && <CheckCircleOutlined style={{ color: '#3fb950', fontSize: 14 }} />}
                                        {entry.status === 'error' && <CloseCircleOutlined style={{ color: '#f85149', fontSize: 14 }} />}
                                        {entry.status === 'pending' && <FileImageOutlined style={{ color: '#8b949e', fontSize: 14 }} />}
                                        <span style={{ color: '#c9d1d9', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {entry.name}
                                        </span>
                                    </div>
                                    {entry.status === 'pending' && (
                                        <Button
                                            type="text" size="small"
                                            icon={<CloseOutlined style={{ fontSize: 10, color: '#8b949e' }} />}
                                            onClick={() => handleRemoveFile(entry.id)}
                                            style={{ minWidth: 24, padding: 0 }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload progress bar */}
                    {uploading && uploadProgress.total > 0 && (
                        <Progress
                            percent={Math.round((uploadProgress.current / uploadProgress.total) * 100)}
                            size="small"
                            style={{ marginTop: 12 }}
                            strokeColor="#1890ff"
                        />
                    )}
                </div>
            </Modal>
        </Layout>
    );
};

export default Dashboard;
